/* eslint-env jest */
import shouldShowDesktopSiteBanner, {
  DESKTOP_LAYOUT_MIN_WIDTH,
  DISMISS_KEY,
  readDismissed,
  markDismissed,
} from './shouldShowDesktopSiteBanner';

// A minimal in-memory sessionStorage stand-in for the pure dismissal helpers.
const fakeStorage = (initial = {}) => {
  const store = { ...initial };
  return {
    getItem: (k) => (k in store ? store[k] : null),
    setItem: (k, v) => { store[k] = String(v); },
  };
};

// A storage whose access throws — Safari private mode / disabled storage.
const throwingStorage = () => ({
  getItem: () => { throw new Error('blocked'); },
  setItem: () => { throw new Error('blocked'); },
});

// Regression guard for the 2026-06 Android "ordering screens show the desktop
// interface" report. Root cause was NOT device detection — the customer app is
// responsive by viewport width, and "Desktop site" mode reports a desktop-class
// width (and spoofs the UA). This banner nudges those users. The detection must
// fire for a touch device at desktop width and stay silent for real desktops and
// normal phones.
describe('shouldShowDesktopSiteBanner', () => {
  it('shows for a touch device rendering at desktop width (Desktop Site mode on)', () => {
    expect(shouldShowDesktopSiteBanner({ maxTouchPoints: 5, innerWidth: 1080 })).toBe(true);
  });

  it('stays hidden on a normal phone (touch + mobile-width viewport)', () => {
    expect(shouldShowDesktopSiteBanner({ maxTouchPoints: 5, innerWidth: 412 })).toBe(false);
  });

  it('stays hidden on a real desktop (no touch, wide viewport)', () => {
    expect(shouldShowDesktopSiteBanner({ maxTouchPoints: 0, innerWidth: 1440 })).toBe(false);
  });

  it('detects touch via the ontouchstart fallback when maxTouchPoints is unavailable', () => {
    expect(shouldShowDesktopSiteBanner({ maxTouchPoints: 0, hasTouchStart: true, innerWidth: 1000 })).toBe(true);
  });

  it('stays hidden once dismissed, even on a touch device at desktop width', () => {
    expect(shouldShowDesktopSiteBanner({ maxTouchPoints: 5, innerWidth: 1080, dismissed: true })).toBe(false);
  });

  it('uses 992 as the threshold (mobile just below, desktop at/above)', () => {
    expect(DESKTOP_LAYOUT_MIN_WIDTH).toBe(992);
    expect(shouldShowDesktopSiteBanner({ maxTouchPoints: 5, innerWidth: 991 })).toBe(false);
    expect(shouldShowDesktopSiteBanner({ maxTouchPoints: 5, innerWidth: 992 })).toBe(true);
  });

  it('handles being called with no arguments', () => {
    expect(shouldShowDesktopSiteBanner()).toBe(false);
  });
});

describe('dismissal storage helpers', () => {
  it('readDismissed is false when nothing has been stored (banner shows)', () => {
    expect(readDismissed(fakeStorage())).toBe(false);
  });

  it('readDismissed is true once the dismissed flag is present (pre-dismissed)', () => {
    expect(readDismissed(fakeStorage({ [DISMISS_KEY]: '1' }))).toBe(true);
  });

  it('markDismissed persists the flag so a later readDismissed returns true', () => {
    const storage = fakeStorage();
    markDismissed(storage);
    expect(storage.getItem(DISMISS_KEY)).toBe('1');
    expect(readDismissed(storage)).toBe(true);
  });

  it('degrades safely when storage is unavailable or throws (private mode)', () => {
    expect(readDismissed(null)).toBe(false);
    expect(readDismissed(throwingStorage())).toBe(false);
    expect(() => markDismissed(null)).not.toThrow();
    expect(() => markDismissed(throwingStorage())).not.toThrow();
  });
});
